import { useState } from "react";
import type { DiscountCode } from "@cliqsuite/db";
import type { Plan } from "../lib/adminTypes";
import type { DiscountCodeFormValue } from "../state/AdminDataContext";
import { Chips, Field, Modal, SwitchRow } from "./Modal";

function randomCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 8; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export function DiscountCodeModal({
  code,
  plans,
  onClose,
  onSubmit,
}: {
  code: DiscountCode | null;
  plans: Plan[];
  onClose: () => void;
  onSubmit: (form: DiscountCodeFormValue) => void;
}) {
  const [codeText, setCodeText] = useState(code?.code ?? randomCode());
  const [planId, setPlanId] = useState(code?.plan_id ?? "");
  const [discountType, setDiscountType] = useState<"percent" | "flat">(code?.discount_type ?? "percent");
  const [value, setValue] = useState(String(code?.value ?? 20));
  const [maxRedemptions, setMaxRedemptions] = useState(code?.max_redemptions != null ? String(code.max_redemptions) : "");
  const [label, setLabel] = useState(code?.label ?? "");
  const [expiresAt, setExpiresAt] = useState(code?.expires_at ?? "");
  const [isActive, setIsActive] = useState(code?.is_active ?? true);

  const valueNum = Math.max(1, Math.round(Number(value) || 0));
  const valid = codeText.trim().length >= 3;

  const submit = () => {
    if (!valid) return;
    onSubmit({
      code: codeText.trim(),
      plan_id: planId || null,
      discount_type: discountType,
      value: discountType === "percent" ? Math.min(100, valueNum) : valueNum,
      max_redemptions: maxRedemptions.trim() ? Math.max(1, Math.round(Number(maxRedemptions))) : null,
      label: label.trim(),
      is_active: isActive,
      expires_at: expiresAt || null,
    });
  };

  return (
    <Modal
      onClose={onClose}
      width={500}
      title={code ? "Edit discount code" : "New discount code"}
      sub="Businesses type it when they pay"
      footer={
        <>
          <button type="button" className="o-btn o-btn--grey" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="o-btn o-btn--ink" onClick={submit} disabled={!valid}>
            <span className="ms" aria-hidden="true">check</span>
            {code ? "Save changes" : "Create code"}
          </button>
        </>
      }
    >
      <Field label="Code">
        <div style={{ display: "flex", gap: 8 }}>
          <input
            className="o-input"
            aria-label="Code"
            value={codeText}
            onChange={(e) => setCodeText(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
            style={{ fontFamily: "monospace", letterSpacing: 1, flex: 1 }}
          />
          <button type="button" className="o-btn o-btn--grey" style={{ height: 48 }} onClick={() => setCodeText(randomCode())}>
            <span className="ms" aria-hidden="true">casino</span>
            Generate
          </button>
        </div>
      </Field>
      <Field label="Label (optional)">
        <input className="o-input" aria-label="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Referral reward" />
      </Field>
      <Chips label="Plan" value={planId} onChange={setPlanId} options={[{ value: "", label: "All plans" }, ...plans.map((p) => ({ value: p.id, label: p.name }))]} />
      <Chips
        label="Type"
        value={discountType}
        onChange={setDiscountType}
        options={[
          { value: "percent", label: "% off" },
          { value: "flat", label: "MMK off" },
        ]}
      />
      <div className="o-fields2">
        <Field label="Discount">
          <input className="o-input num" aria-label="Discount" type="number" min={1} max={discountType === "percent" ? 100 : undefined} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
        <Field label="Max redemptions">
          <input className="o-input num" aria-label="Max redemptions" type="number" min={1} value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value.replace(/\D/g, ""))} placeholder="Unlimited" />
        </Field>
      </div>
      {code && code.redeemed_count > 0 && (
        <div className="o-row__sub" style={{ margin: "-8px 0 16px" }}>
          Already redeemed {code.redeemed_count} time{code.redeemed_count === 1 ? "" : "s"}.
        </div>
      )}
      <Field label="Expires (optional)">
        <input className="o-input" aria-label="Expires (optional)" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
      </Field>
      <SwitchRow label="Active" checked={isActive} onChange={setIsActive} />
    </Modal>
  );
}
